import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const INSTALLS = [
  {
    photo: "/images/install-rinnai-2.webp",
    alt: "Installed Rinnai Enviroflo heat pump hot water unit at a Victorian home",
    caption: "Rinnai Enviroflo – recent install",
  },
  {
    photo: "/images/install-rinnai.webp",
    alt: "Installed Rinnai heat pump hot water unit at a Victorian home",
    caption: "Rinnai heat pump – recent install",
  },
  {
    photo: "/images/rinnai-after.webp",
    alt: "Installed Rinnai heat pump hot water unit beside a brick wall",
    caption: "Rinnai heat pump – recent install",
  },
];

const Installations = () => {
  const trackRef = useRef(null);

  // Auto-advance on desktop only; mobile users swipe natively.
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!desktop.matches || reduceMotion.matches) return;
    const id = setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const step = el.clientWidth / 3 + 24;
      const next = el.scrollLeft + step;
      el.scrollTo({ left: next >= max - 10 ? 0 : next, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="installations-section"
      className="bg-cream py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gdark mb-4 flex items-center gap-2">
            <span className="inline-block w-8 h-px bg-gdark" />
            Our work around Victoria
          </p>
          <h2 className="font-serif font-bold text-navy tracking-tight text-4xl md:text-5xl leading-tight">
            Recent Installations
          </h2>
        </motion.div>

        <div
          ref={trackRef}
          data-testid="install-carousel-track"
          className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {INSTALLS.map((item, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`install-card-${i}`}
              className="group shrink-0 w-[85%] sm:w-[46%] lg:w-[31.5%] snap-start bg-white border border-navy/10 rounded-[20px] p-3 hover:shadow-xl transition-shadow duration-300"
              style={{ boxShadow: "0 20px 45px rgba(1,27,71,0.10)" }}
            >
              <div className="overflow-hidden rounded-lg aspect-[4/5] bg-cream">
                <img
                  src={item.photo}
                  alt={item.alt}
                  data-testid={`install-photo-${i}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                  loading="lazy"
                />
              </div>
              <figcaption
                data-testid={`install-caption-${i}`}
                className="px-2 pt-3 pb-1 min-h-[3.5rem]"
              >
                <span className="block text-xs font-medium uppercase tracking-[0.2em] text-navy-muted group-hover:text-navy group-hover:-translate-y-0.5 transition-[color,transform] duration-300">
                  {item.caption}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
        <p
          data-testid="install-carousel-hint"
          className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-muted/60 lg:hidden"
        >
          Swipe to see more
        </p>
      </div>
    </section>
  );
};

export default Installations;
